import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Add20Regular, Edit20Regular } from "@fluentui/react-icons";
import { PageHeader } from "../../components/PageHeader";
import { ListToolbar } from "../../components/ListToolbar";
import { formatCurrency, formatDateShort } from "../Obras/obraFormat";
import {
  getRentalContracts,
  getRentalContractStatuses,
  type RentalContract,
  type RentalContractStatus,
} from "../../arrendamentos/arrendamentosApi";
import {
  daysUntil,
  isExpiringSoon,
  rentalStatusBadgeClass,
  RENTAL_EXPIRY_WINDOW_DAYS,
} from "../../arrendamentos/arrendamentoFormat";

const errorMessage = (err: unknown, fallback: string): string =>
  err instanceof Error && err.message ? err.message : fallback;

export function ArrendamentosView() {
  const navigate = useNavigate();

  const [contracts, setContracts] = useState<RentalContract[]>([]);
  const [statuses, setStatuses] = useState<RentalContractStatus[]>([]);
  const [statusFilter, setStatusFilter] = useState("");
  const [filter, setFilter] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await getRentalContracts(
        statusFilter ? { statusCode: statusFilter } : undefined,
      );
      setContracts(data);
    } catch (err) {
      setError(
        errorMessage(err, "Não foi possível carregar os contratos de arrendamento."),
      );
    } finally {
      setIsLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    getRentalContractStatuses()
      .then(setStatuses)
      .catch(() => setStatuses([]));
  }, []);

  const normalizedFilter = filter.trim().toLowerCase();
  const shownContracts =
    normalizedFilter === ""
      ? contracts
      : contracts.filter((c) =>
          [c.propertyName, c.tenantName, c.contractNumber ?? ""]
            .join(" ")
            .toLowerCase()
            .includes(normalizedFilter),
        );

  // Alerta de fim de prazo [5.2.2] — independente do filtro de estado atual: conta
  // sobre a lista carregada quando não há filtro; caso contrário só sobre as ativas visíveis.
  const expiringCount = useMemo(
    () => contracts.filter((c) => isExpiringSoon(c.statusCode, c.endDate)).length,
    [contracts],
  );

  const statusChips = [{ code: "", name: "Todos" }, ...statuses.map((s) => ({ code: s.code, name: s.name }))];

  const actions = (
    <button
      className="px-4 py-2 bg-primary hover:bg-primary/90 text-primary-foreground rounded transition-colors flex items-center gap-2 min-h-[44px]"
      style={{
        borderRadius: "var(--radius-button)",
        height: "var(--button-height)",
      }}
      onClick={() => navigate("/arrendamentos/novo")}
    >
      <Add20Regular className="w-5 h-5" />
      Novo contrato
    </button>
  );

  return (
    <div className="flex flex-col h-full">
      <PageHeader
        title="Arrendamentos"
        subtitle="Contratos de arrendamento e alertas de fim de prazo"
        actions={actions}
      />

      <div className="flex-1 overflow-y-auto p-4 md:p-6 space-y-4">
        {error && (
          <div
            className="border border-border bg-destructive/10 text-destructive px-4 py-3 text-sm"
            style={{ borderRadius: "var(--radius)" }}
          >
            {error}
          </div>
        )}

        {expiringCount > 0 && (
          <div
            className="border border-border bg-yellow-500/10 text-yellow-700 px-4 py-3 text-sm"
            style={{ borderRadius: "var(--radius)" }}
          >
            {expiringCount} contrato(s) a terminar nos próximos{" "}
            {RENTAL_EXPIRY_WINDOW_DAYS} dias.
          </div>
        )}

        <ListToolbar
          filter={filter}
          onFilterChange={setFilter}
          placeholder="Filtrar por imóvel, inquilino ou nº de contrato…"
          summary={
            <span className="text-secondary-text text-sm">
              {shownContracts.length} contrato(s)
            </span>
          }
        />

        <div className="flex items-center gap-2 flex-wrap">
          {statusChips.map((chip) => {
            const isActive = statusFilter === chip.code;
            return (
              <button
                key={chip.code || "all"}
                type="button"
                onClick={() => setStatusFilter(chip.code)}
                className={`inline-flex items-center px-3 py-1 text-sm cursor-pointer ${
                  isActive
                    ? "bg-primary text-primary-foreground"
                    : "border border-border text-foreground hover:bg-accent/50"
                }`}
                style={{ borderRadius: "var(--radius-button)" }}
              >
                {chip.name}
              </button>
            );
          })}
        </div>

        <div
          className="bg-card border border-border overflow-x-auto"
          style={{ borderRadius: "var(--radius-card)" }}
        >
          <table className="w-full" style={{ minWidth: 900 }}>
            <thead>
              <tr className="border-b border-border bg-muted text-left">
                <th className="px-4 py-3">Imóvel</th>
                <th className="px-4 py-3">Inquilino</th>
                <th className="px-4 py-3" style={{ width: 120 }}>
                  Início
                </th>
                <th className="px-4 py-3" style={{ width: 160 }}>
                  Fim
                </th>
                <th className="px-4 py-3 text-right" style={{ width: 140 }}>
                  Renda mensal
                </th>
                <th className="px-4 py-3" style={{ width: 140 }}>
                  Estado
                </th>
                <th className="px-4 py-3" style={{ width: 100 }}>
                  Ações
                </th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td
                    colSpan={7}
                    className="px-4 py-6 text-center text-secondary-text"
                  >
                    A carregar…
                  </td>
                </tr>
              ) : shownContracts.length === 0 ? (
                <tr>
                  <td
                    colSpan={7}
                    className="px-4 py-6 text-center text-secondary-text"
                  >
                    {filter !== "" || statusFilter !== ""
                      ? "Sem contratos para o filtro."
                      : "Sem contratos de arrendamento. Crie o primeiro contrato."}
                  </td>
                </tr>
              ) : (
                shownContracts.map((c) => {
                  const days = daysUntil(c.endDate);
                  const expiring = isExpiringSoon(c.statusCode, c.endDate);
                  return (
                    <tr
                      key={c.id}
                      className="border-b border-border last:border-b-0 hover:bg-accent/30"
                    >
                      <td
                        className="px-4 py-3 break-words"
                        style={{ fontWeight: "var(--font-weight-medium)" }}
                      >
                        {c.propertyName}
                        {c.contractNumber && (
                          <span className="block text-xs text-secondary-text mt-0.5">
                            Nº {c.contractNumber}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 break-words">{c.tenantName}</td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        {formatDateShort(c.startDate)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        {c.endDate ? (
                          formatDateShort(c.endDate)
                        ) : (
                          <span className="text-secondary-text">Sem termo</span>
                        )}
                        {expiring && (
                          <span
                            className="inline-flex items-center px-2 py-0.5 text-xs bg-yellow-500/10 text-yellow-700 mt-1"
                            style={{ borderRadius: "var(--radius)" }}
                          >
                            Termina em {days} dia(s)
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right whitespace-nowrap">
                        {formatCurrency(c.monthlyRent)}
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`inline-flex items-center px-2 py-1 text-xs ${rentalStatusBadgeClass(
                            c.statusCode,
                          )}`}
                          style={{ borderRadius: "var(--radius)" }}
                        >
                          {c.statusName}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <button
                          type="button"
                          onClick={() =>
                            navigate(`/arrendamentos/editar/${c.id}`)
                          }
                          className="inline-flex items-center gap-1 text-primary text-sm cursor-pointer"
                        >
                          <Edit20Regular className="w-4 h-4" />
                          Editar
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
